import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal, Carousel } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'

import useApi from '../../hooks/useApi'
import { isVideo } from '../../lib/files'
import { formatLocation } from '../../lib/address'
import { sortCategoriesAlphabetically } from '../../lib/sort'
import { getTeam } from '../../lib/event'
import Icon from '../icon'
import VPFBadge from '../VPFBadge/VPFBadge'
import styles from './EventSlideshowModal.module.scss'

export default function EventSlideshowModal({
  show,
  onHide,
  eventId,
  eventSlug,
  initialIndex = 0,
}) {
  const { t, i18n } = useTranslation()
  const { getEvent, getEventAcceptedPledges } = useApi()
  const locale = i18n.language ?? 'en'

  const [event, setEvent] = useState(null)
  const [acceptedPledges, setAcceptedPledges] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [activeIndex, setActiveIndex] = useState(initialIndex)

  useEffect(() => {
    if (!show) return

    let cancelled = false
    const idOrSlug = eventSlug || eventId
    if (!idOrSlug) return

    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const { data } = await getEvent({ id: idOrSlug })
        if (!cancelled && data) {
          setEvent(data)
          if (
            Array.isArray(data?.media_attachments) &&
            data.media_attachments.length > 0
          ) {
            const boundedIndex = Math.max(
              0,
              Math.min(initialIndex, data.media_attachments.length - 1),
            )
            setActiveIndex(boundedIndex)
          } else {
            setActiveIndex(0)
          }
          try {
            const { data: pledges } = await getEventAcceptedPledges(data.id)
            if (!cancelled && Array.isArray(pledges))
              setAcceptedPledges(pledges)
          } catch {
            // optional
          }
        }
      } catch (e) {
        if (!cancelled) setError(e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    show,
    eventId,
    eventSlug,
    getEvent,
    getEventAcceptedPledges,
    initialIndex,
  ])

  const attachments = useMemo(
    () => (event?.media_attachments ? event.media_attachments : []),
    [event?.media_attachments],
  )

  const handleSelect = index => {
    setActiveIndex(index)
  }

  const startMoment = useMemo(
    () =>
      event?.event_start_date
        ? moment(event.event_start_date).tz(moment.tz.guess())
        : null,
    [event?.event_start_date],
  )

  const locationText = useMemo(() => {
    if (!event) return ''
    if (event.remote) return t('event.remote')
    return formatLocation(event.location) || ''
  }, [event, t])

  const title = event?.title ?? t('event.title', 'Event')

  const organizer = event?.user
  const organizerName = organizer?.name ?? ''
  const organizerLogoUrl =
    organizer?.media_attachment?.thumbnail_url ??
    organizer?.media_attachment?.direct_upload_url

  const categories = useMemo(
    () => sortCategoriesAlphabetically(event?.categories ?? [], locale) || [],
    [event?.categories, locale],
  )

  const participantCount = useMemo(() => {
    if (!event) return 0
    const volunteers = event.volunteer ?? event.participants ?? []
    let count = volunteers.length
    try {
      const team = getTeam(event)
      const volunteerIds = new Set(
        volunteers.map(v => v.user?.id ?? v.id).filter(Boolean),
      )
      team.forEach(m => {
        if (!volunteerIds.has(m.id)) count += 1
      })
    } catch {
      // event may not have admins/tasks
    }
    return count
  }, [event])

  const commentsCount = event?.comments_count ?? 0

  const totalHours = event?.total_hours ?? event?.hours_worked ?? null
  const vpfCiv = event?.vpf_total_civ ?? event?.volunteer_pay_forward ?? null
  const likesCount = event?.likes_count ?? event?.likes_count ?? 0
  const primarySponsor = acceptedPledges?.[0]?.business
  const isVpfEvent = !!event?.volunteer_pay_forward

  /** Same path as CommunityFootprintMap "View event" (`/events/${slug || id}`), full URL for clipboard. */
  const eventShareUrl = useMemo(() => {
    if (!event) return ''
    const path = `/events/${event.slug ?? event.id}`
    if (typeof window === 'undefined') return path
    return `${window.location.origin}${path}`
  }, [event])

  const handleShareClick = useCallback(() => {
    if (!eventShareUrl) return
    navigator.clipboard.writeText(eventShareUrl).then(
      () => {
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(
            t('event.share.modal.copiedToClipboard', 'Copied to Clipboard'),
          )
        }
      },
      () => {},
    )
  }, [eventShareUrl, t])

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      dialogClassName={styles.modalDialog}
      contentClassName={styles.modalContent}
      backdropClassName={styles.modalBackdrop}
    >
      <Modal.Body className={styles.modalBody}>
        {loading && (
          <div className={styles.loading}>
            {t('profiles.mapLoading', 'Loading…')}
          </div>
        )}
        {error && !loading && (
          <div className={styles.loading}>
            {t('profiles.mapLoadError', 'Content could not be loaded.')}
          </div>
        )}
        {!loading && !error && event && (
          <div className={styles.layout}>
            <div className={styles.topOverlay}>
              <div className={styles.organizerRow}>
                {primarySponsor?.media_attachment ? (
                  <img
                    src={
                      primarySponsor.media_attachment.thumbnail_url ??
                      primarySponsor.media_attachment.direct_upload_url
                    }
                    alt=""
                    className={styles.organizerLogo}
                  />
                ) : organizerLogoUrl ? (
                  <img
                    src={organizerLogoUrl}
                    alt=""
                    className={styles.organizerLogo}
                  />
                ) : null}
                <span className={styles.organizerName}>
                  {primarySponsor?.name ||
                    organizerName ||
                    t('event.organizer', 'Organizer')}
                </span>
              </div>
              <button
                type="button"
                className={styles.closeButton}
                onClick={onHide}
                aria-label={t('common.close', 'Close')}
              >
                <Icon name="times" />
              </button>
            </div>

            <div className={styles.mediaWrap}>
              {attachments.length > 0 ? (
                <Carousel
                  activeIndex={activeIndex}
                  onSelect={handleSelect}
                  interval={null}
                  indicators={false}
                  nextLabel={t('slideshow.next', 'Next')}
                  prevLabel={t('slideshow.previous', 'Previous')}
                  prevIcon={
                    <Icon
                      name="chevron-left"
                      className={styles.carouselArrowIcon}
                    />
                  }
                  nextIcon={
                    <Icon
                      name="chevron-right"
                      className={styles.carouselArrowIcon}
                    />
                  }
                  className={styles.carousel}
                >
                  {attachments.map((media, index) => (
                    <Carousel.Item key={media.id ?? index}>
                      {isVideo(media) ? (
                        <div className={styles.videoWrapper}>
                          <video
                            controls
                            poster={media.thumbnail_url}
                            src={media.direct_upload_url}
                            className={styles.media}
                          />
                        </div>
                      ) : (
                        <img
                          src={media.direct_upload_url}
                          alt={title}
                          className={styles.media}
                        />
                      )}
                    </Carousel.Item>
                  ))}
                </Carousel>
              ) : (
                <div className={styles.noMedia}>
                  {t('event.detailsDefault', 'Event details will appear here.')}
                </div>
              )}

              <div className={styles.detailsOverlay}>
                {categories.length > 0 && (
                  <div className={styles.causesRow}>
                    <span className={styles.causesLabel}>
                      {t('slideshow.causes', 'causes')}
                    </span>
                    <div className={styles.causesIcons}>
                      {categories.slice(0, 5).map(cat => (
                        <Icon
                          key={cat.id}
                          name={cat.icon || 'hand-heart'}
                          className={styles.causeIcon}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div className={styles.statsRow}>
                  {participantCount > 0 && (
                    <span>
                      {participantCount}{' '}
                      {t('slideshow.volunteers', 'volunteers')}
                    </span>
                  )}
                  {totalHours != null && Number(totalHours) > 0 && (
                    <span>
                      {totalHours} {t('slideshow.hours', 'hours')}
                    </span>
                  )}
                  {vpfCiv != null && Number(vpfCiv) > 0 && (
                    <span>VPF {vpfCiv} CIV</span>
                  )}
                </div>
                <h2 className={styles.overlayTitle}>{title}</h2>
                {startMoment && (
                  <p className={styles.overlayDate}>
                    {startMoment.format(t('config.date_format', 'MMM D, YYYY'))}
                  </p>
                )}
                {locationText && (
                  <p className={styles.overlayLocation}>{locationText}</p>
                )}
              </div>
            </div>

            {attachments.length > 1 && (
              <div className={styles.indicatorsRow}>
                {attachments.map((_, index) => (
                  <span
                    key={index}
                    className={
                      index === activeIndex
                        ? styles.indicatorActive
                        : styles.indicator
                    }
                  />
                ))}
              </div>
            )}

            <div className={styles.footer}>
              <div className={styles.footerLeft}>
                {isVpfEvent ? (
                  <span className={styles.vpfBy}>
                    <VPFBadge
                      event={event}
                      hideText={false}
                      iconColor="#000"
                      className={styles.vpfFooterBadge}
                      onVPFClick={() => {}}
                    />
                    <span className={styles.vpfByText}>
                      {t('slideshow.by', 'by')}{' '}
                      {primarySponsor ? (
                        <Link
                          to={`/profile/business/${
                            primarySponsor.slug ?? primarySponsor.id
                          }`}
                          className={styles.footerLink}
                        >
                          {primarySponsor.name}
                        </Link>
                      ) : (
                        <span>
                          {organizerName || t('event.organizer', 'Organizer')}
                        </span>
                      )}
                    </span>
                  </span>
                ) : (
                  <>
                    {primarySponsor && (
                      <>
                        {primarySponsor.media_attachment && (
                          <img
                            src={
                              primarySponsor.media_attachment.thumbnail_url ??
                              primarySponsor.media_attachment.direct_upload_url
                            }
                            alt=""
                            className={styles.footerLogoBlack}
                          />
                        )}
                        <span className={styles.footerSupport}>
                          {t('slideshow.inSupportOf', 'in support of')}{' '}
                          <Link
                            to={`/profile/business/${
                              primarySponsor.slug ?? primarySponsor.id
                            }`}
                            className={styles.footerLink}
                          >
                            {primarySponsor.name}
                          </Link>
                        </span>
                      </>
                    )}
                    {!primarySponsor && (
                      <span className={styles.footerSupport}>
                        {t('slideshow.inSupportOf', 'in support of')} —
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className={styles.footerRight}>
                <span className={styles.reaction}>
                  <Icon name="comment-smile" className={styles.reactionIcon} />
                  {commentsCount} {t('slideshow.comments', 'comments')}
                </span>
                <span className={styles.reaction}>
                  <Icon name="heart" className={styles.reactionIcon} />
                  {likesCount} {t('slideshow.likes', 'likes')}
                </span>
                <button
                  type="button"
                  className={`${styles.reaction} ${styles.shareButton}`}
                  onClick={handleShareClick}
                  aria-label={t('event.share.title', 'Share')}
                >
                  <Icon name="share" className={styles.reactionIcon} />
                  {t('slideshow.share', 'share')}
                </button>
                <button
                  type="button"
                  className={styles.ellipsisButton}
                  aria-label={t('common.more', 'More')}
                >
                  <Icon name="ellipsis" />
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

EventSlideshowModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  eventSlug: PropTypes.string,
  initialIndex: PropTypes.number,
}
